"use no memo";
import {
	createFileRoute,
	Outlet,
	redirect,
	useLocation,
	useNavigate,
} from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { CreateOrganizationUserModal } from "@/components/Organisms/CreateOrganizationUserModal/CreateOrganizationUserModal";
import { CreatePlatformUserModal } from "@/components/Organisms/CreatePlatformUserModal/CreatePlatformUserModal";
import { PageHeader } from "@/components/Organisms/PageHeader/PageHeader";
import { PageLayout } from "@/components/Organisms/PageLayout/PageLayout";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useEntityActionAllowed } from "@/hooks/use-entity-action-allowed";
import { ViewTransitionTypes, viewTransition } from "@/utils/view-transitions";

export const Route = createFileRoute(
	"/_authenticated/(admin-app)/_admin/admin/users",
)({
	component: RouteComponent,
	beforeLoad: ({ location }) => {
		// ✅ Use location from route context
		if (
			location.pathname === "/admin/users" ||
			location.pathname === "/admin/users/"
		) {
			console.log("Redirecting to organization");
			// Redirect to default tab (organization)
			throw redirect({
				to: "/admin/users/organization",
				replace: true,
				...viewTransition(ViewTransitionTypes.FADE),
			});
		}
	},
	validateSearch: (search) => {
		return z
			.object({
				create: z.enum(["platform", "organization"]).optional(),
				filterOpen: z.boolean().optional(),
			})
			.parse(search);
	},
});

function RouteComponent() {
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const location = useLocation();

	// Extract current tab from the pathname with more robust matching
	const getCurrentTab = (): "organization" | "platform" => {
		// Check if pathname includes the tab name (covers child routes too)
		if (location.pathname.includes("/admin/users/platform")) {
			return "platform";
		}
		if (location.pathname.includes("/admin/users/organization")) {
			return "organization";
		}
		// Default to organization if no match (matches redirect behavior)
		return "organization";
	};

	const currentTab = getCurrentTab();

	const tabs = [
		{ label: t("admin.users.tabs.organization.label"), id: "organization" },
		{ label: t("admin.users.tabs.platform.label"), id: "platform" },
	];

	const [openPlatform, setPlatformOpen] = useState(false);
	const [openOrganization, setOrganizationOpen] = useState(false);
	const canCreatePlatformUser = useEntityActionAllowed({
		name: "CreateSystemUser",
	});
	const canCreateOrganizationUser = useEntityActionAllowed({
		name: "CreateOrganizationUser",
	});
	const canCreateCurrentUser =
		currentTab === "platform"
			? canCreatePlatformUser.allowed
			: canCreateOrganizationUser.allowed;

	return (
		<>
			<PageLayout
				header={
					<PageHeader
						type="landing"
						title={t("admin.users.tabs.table.title")}
						description={
							currentTab === "platform"
								? t("admin.users.tabs.table.platformDescription")
								: t("admin.users.tabs.table.organizationDescription")
						}
						actions={
							canCreateCurrentUser
								? [
										{
											label:
												currentTab === "platform"
													? t(
															"admin.users.tabs.table.actions.createPlatformUser",
														)
													: t(
															"admin.users.tabs.table.actions.createOrganizationUser",
														),
											testId:
												currentTab === "platform"
													? "platform-user-create-open-btn"
													: "org-user-create-open-btn",
											color: "accent",
											onPress: () => {
												if (currentTab === "platform") {
													setPlatformOpen(true);
													navigate({
														to: "/admin/users/platform",
														search: { create: "platform" },
													});
												} else {
													setOrganizationOpen(true);
													navigate({
														to: "/admin/users/organization",
														search: { create: "organization" },
													});
												}
											},
										},
									]
								: undefined
						}
						showTabs
						tabs={tabs}
						selectedTabId={currentTab}
						onTabChange={(chosen) => {
							if (chosen === "organization") {
								navigate({
									to: "/admin/users/organization",
									...viewTransition(ViewTransitionTypes.SLIDE_LEFT),
								});
							} else if (chosen === "platform") {
								navigate({
									to: "/admin/users/platform",
									...viewTransition(ViewTransitionTypes.SLIDE_RIGHT),
								});
							}
						}}
					/>
				}
			>
				<Outlet />
			</PageLayout>

			{canCreatePlatformUser.allowed ? (
			<CreatePlatformUserModal
				isOpen={openPlatform}
				setIsOpen={(o) => {
					setPlatformOpen(o);
					if (!o)
						navigate({
							to: "/admin/users/platform",
						});
				}}
				handleClose={() => {
					setPlatformOpen(false);
					navigate({
						to: "/admin/users/platform",
					});
				}}
			/>
			) : null}

			{canCreateOrganizationUser.allowed ? (
			<CreateOrganizationUserModal
				isOpen={openOrganization}
				setIsOpen={(o) => {
					setOrganizationOpen(o);
					if (!o)
						navigate({
							to: "/admin/users/organization",
						});
				}}
				handleClose={() => {
					setOrganizationOpen(false);
					navigate({
						to: "/admin/users/organization",
					});
				}}
			/>
			) : null}
		</>
	);
}
